/**
 * "Your account has been deactivated" - the pop-up a deactivated person gets
 * instead of an error on every panel.
 *
 * It wears the critical classic announcement card (`announcements.ts`), because
 * it is the same kind of message: one the person must read. Like a critical
 * announcement it has no close, no Esc and no scrim click - behind it every
 * request is refused anyway, so there is nothing to go back to. The one way
 * out is signing out, to come back as somebody else or not at all.
 *
 * The reason is Root's own words from Administration → Users, when Root wrote
 * one (`iam.app_user.deactivation_reason`, migration `0036`).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { accountDeactivated } from '../../state/account-status.ts';
import { signOut } from '../../auth/auth.ts';
import { isDevIdentity } from '../../auth/dev-identity.ts';
import { toneIcon } from './announcements.ts';

@customElement('account-deactivated')
export class AccountDeactivatedPopup extends LightElement {
  private store = new StoreController(this, [accountDeactivated]);

  override updated(): void {
    this.querySelector<HTMLElement>('.ann-primary')?.focus({ preventScroll: true });
  }

  override render(): TemplateResult | typeof nothing {
    const state = accountDeactivated.value;
    if (!state) return nothing;
    return html`<div class="ann-scrim">
      <div class="ann-modal ann-classic tone-critical" role="alertdialog" aria-modal="true"
        aria-labelledby="deactivated-title" aria-describedby="deactivated-message">
        <div class="ann-modal-body">
          <div class="ann-icon-disc">${toneIcon('critical', 20)}</div>
          <h2 class="ann-title" id="deactivated-title">Your account has been deactivated</h2>
          <p class="ann-message" id="deactivated-message">You can no longer use Data Explorer with this account. If you think this is a mistake, contact a Root administrator.</p>
          ${state.reason
            ? html`<div class="ann-reason">
                <span class="ann-reason-label">Reason given</span>
                <p>${state.reason}</p>
              </div>`
            : nothing}
        </div>
        <div class="ann-modal-foot">
          <span class="ann-spacer"></span>
          ${isDevIdentity()
            // A local persona cannot sign out, and the scrim covers the persona
            // menu: close it, so the developer can switch to another persona.
            ? html`<button type="button" class="btn btn-primary ann-primary"
                @click=${() => accountDeactivated.set(null)}>Close (local persona)</button>`
            : html`<button type="button" class="btn btn-primary ann-primary" @click=${() => void signOut()}>Sign out</button>`}
        </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'account-deactivated': AccountDeactivatedPopup;
  }
}
